import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Container,
  CircularProgress,
} from '@mui/material';
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from 'react-router-dom';

import { useUserAuth } from '../../context/UserAuthContext';
import Header from '../../components/Header/Header';
import Logo from '../../components/Logo/Logo';
import FilterBar from './FilterBar';
import Dashboard from './Dashboard';
import AutomationsDashboard from './AutomationsDashboard';
import ExceptionsLogDialog from './ExceptionsLogDialog';
import ManageSourcesDialog from './ManageSourcesDialog';

import './DataIntegrity2.css';

const DataIntegrity2 = () => {
  const navigate = useNavigate();
  const { user: firebaseUser, loading: authenticating } = useUserAuth();

  const [stores, setStores] = useState([
    { storeId: 'all', storeName: 'All Stores' },
  ]);
  const [selectedStore, setSelectedStore] = useState('all');
  const [showAlert, setShowAlert] = useState('');
  const [exceptionsLogOpen, setExceptionsLogOpen] = useState(false);
  const [manageSourcesOpen, setManageSourcesOpen] = useState(false);
  const [activeView, setActiveView] = useState('sources'); // 'sources' or 'automations'
  const [triggerAddAutomation, setTriggerAddAutomation] = useState(0);

  // loadKey: incremented to trigger Dashboard per-category fetches
  // 0 = no data loaded yet (placeholder mode)
  const [loadKey, setLoadKey] = useState(0);

  // Default date range: yesterday
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d;
  });
  const [endDate, setEndDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d;
  });

  const isTabletOrMobile = useMediaQuery({ maxWidth: 1224 });

  // On mount: fetch user info and stores, then auto-trigger first load
  useEffect(() => {
    if (firebaseUser?.getIdToken) {
      handleInit();
    }
  }, [firebaseUser]);

  const handleInit = async () => {
    try {
      const token = await firebaseUser.getIdToken(true);

      // Check user info (redirect new users to profile)
      const userRes = await axios.get(
        `/hd-api/user/${firebaseUser.email}/info`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (userRes.data.newUser) {
        navigate('/profile');
        return;
      }

      // Fetch stores from DI2 endpoint
      const storesRes = await axios.get('/hd-api/data-integrity-2/stores', {
        headers: { Authorization: `Bearer ${token}` },
      });

      const storeList = storesRes.data.stores || [];
      storeList.unshift({ storeId: 'all', storeName: 'All Stores' });
      setStores(storeList);

      // Trigger initial per-category load in Dashboard
      setLoadKey(1);
    } catch (error) {
      console.error('Error initializing DI2:', error);
    }
  };

  const handleLoad = () => {
    setLoadKey((k) => k + 1);
  };

  let content;
  if (authenticating) {
    content = (
      <div style={{ marginTop: '10%', width: '100%', textAlign: 'center' }}>
        <CircularProgress />
      </div>
    );
  } else {
    content = (
      <Box
        sx={{
          mt: 2,
          backgroundColor: 'rgba(255,255,255,0.85)',
          backdropFilter: 'blur(12px)',
          borderRadius: 3,
          px: { xs: 2, md: 4 },
          py: 3,
        }}
      >
        <FilterBar
          stores={stores}
          selectedStore={selectedStore}
          setSelectedStore={setSelectedStore}
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          onLoad={handleLoad}
          onOpenExceptionsLog={() => setExceptionsLogOpen(true)}
          onOpenManageSources={() => {
            if (activeView === 'sources') {
              setManageSourcesOpen(true);
            } else {
              setTriggerAddAutomation((k) => k + 1);
            }
          }}
          activeView={activeView}
          onViewChange={setActiveView}
        />
        {activeView === 'sources' ? (
          <Dashboard
            firebaseUser={firebaseUser}
            stores={stores}
            startDate={startDate}
            endDate={endDate}
            selectedStore={selectedStore}
            loadKey={loadKey}
          />
        ) : (
          <AutomationsDashboard
            firebaseUser={firebaseUser}
            triggerAddAutomation={triggerAddAutomation}
          />
        )}
      </Box>
    );
  }

  return (
    <div>
      <Header isTabletOrMobile={isTabletOrMobile} />
      <Container maxWidth="xl">
        <Logo isTabletOrMobile={isTabletOrMobile} showAlert={showAlert} />
        {content}
      </Container>
      <ExceptionsLogDialog
        open={exceptionsLogOpen}
        onClose={() => setExceptionsLogOpen(false)}
        firebaseUser={firebaseUser}
      />
      <ManageSourcesDialog
        open={manageSourcesOpen}
        onClose={() => setManageSourcesOpen(false)}
        firebaseUser={firebaseUser}
      />
    </div>
  );
};

export default DataIntegrity2;
